import { useCallback, useMemo, useState } from 'react';
import { message, Tag, Typography } from 'antd';
import {
  EditOutlined, EyeOutlined, PlusOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../components/common/erp-grid';
import { accountsApi } from './accountsApi';
import LedgerModal from './LedgerModal';
import {
  ACCOUNT_SUBTYPE_LABELS,
  LEDGER_ACCOUNT_SUBTYPES,
  SUBTYPE_TAG_COLOR,
  accountTypeFromSubtype
} from './coaClassification';
import useAuth from '../../../hooks/useAuth';
import './accounts.css';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Ledger Accounts (Chart of Accounts).
//
// Lists every ledger in the tenant schema with type / group / normal-
// balance / opening-balance facets. Toolbar carries a "+ New Ledger"
// button gated by `erp.accounts.ledgers.create`. Double-click or the
// row's ⋯ menu opens the LedgerModal in view / edit mode.
//
// System ledgers (the 24 seeded chart-of-accounts rows) are shown
// with a "System" tag and protected from destructive edits inside the
// modal — code / type / normal balance stay immutable; the operator
// can still rename, regroup, or toggle activity.
// ─────────────────────────────────────────────────────────────────

const ADAPTER = {
  list: (query) => accountsApi.listLedgers(query)
};

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load ledger accounts.';
  message.error({ content: msg, key: 'ledger-list' });
}

const ACCOUNT_TYPE_COLORS = {
  asset:     'green',
  liability: 'red',
  equity:    'gold',
  income:    'blue',
  expense:   'orange'
};

const columns = [
  {
    title: 'Code',  dataIndex: 'accountCode', key: 'accountCode',
    sortField: 'account_code', sorter: true,
    width: 180, fixed: 'left', dataType: 'string',
    render: (v) => <code className="erp-grid__code">{v}</code>
  },
  {
    title: 'Name', dataIndex: 'accountName', key: 'accountName',
    sortField: 'account_name', sorter: true,
    width: 240, ellipsis: true, dataType: 'string',
    render: (v) => <span className="erp-grid__primary">{v}</span>
  },
  {
    title: 'Type', dataIndex: 'accountType', key: 'accountType',
    sortField: 'account_type', sorter: true,
    width: 110, dataType: 'enum',
    groupable: true, groupField: 'account_type',
    filters: [
      { text: 'Asset', value: 'asset' },
      { text: 'Liability', value: 'liability' },
      { text: 'Equity', value: 'equity' },
      { text: 'Income', value: 'income' },
      { text: 'Expense', value: 'expense' }
    ],
    filterField: 'account_type',
    render: (v) => (
      <Tag color={ACCOUNT_TYPE_COLORS[v] || 'default'} style={{ marginInlineEnd: 0 }}>
        {v ? v[0].toUpperCase() + v.slice(1) : '—'}
      </Tag>
    )
  },
  {
    title: 'Group', dataIndex: 'accountGroup', key: 'accountGroup',
    sortField: 'account_group', sorter: true,
    width: 180, dataType: 'string',
    groupable: true, groupField: 'account_group',
    render: (v) => v ? <span>{v}</span> : <span className="erp-grid__muted">—</span>
  },
  // Stage 6B.9B — production-grade classification column. Tag colour
  // mirrors the account_type so the operator sees at a glance which
  // bucket the subtype belongs to.
  {
    title: 'Subtype', dataIndex: 'accountSubtype', key: 'accountSubtype',
    sortField: 'account_subtype', sorter: true,
    width: 160, dataType: 'enum',
    groupable: true, groupField: 'account_subtype',
    filters: LEDGER_ACCOUNT_SUBTYPES.map((s) => ({
      text: ACCOUNT_SUBTYPE_LABELS[s] || s, value: s
    })),
    filterField: 'account_subtype',
    render: (v) => {
      if (!v) return <span className="erp-grid__muted">—</span>;
      const t = accountTypeFromSubtype(v);
      return (
        <Tag color={SUBTYPE_TAG_COLOR[t] || 'default'} style={{ marginInlineEnd: 0 }}>
          {ACCOUNT_SUBTYPE_LABELS[v] || v}
        </Tag>
      );
    }
  },
  {
    title: 'Normal Bal.', dataIndex: 'normalBalance', key: 'normalBalance',
    width: 100, align: 'center', dataType: 'enum',
    render: (v) => v === 'debit'
      ? <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>Debit</Tag>
      : <Tag color="purple"   style={{ marginInlineEnd: 0 }}>Credit</Tag>
  },
  {
    title: 'GST', dataIndex: 'gstRelevant', key: 'gstRelevant',
    width: 70, align: 'center', dataType: 'boolean',
    filters: [{ text: 'GST-relevant', value: true }, { text: 'Other', value: false }],
    filterField: 'gst_relevant',
    render: (v) => v ? '✓' : <span className="erp-grid__muted">—</span>
  },
  {
    title: 'System', dataIndex: 'isSystem', key: 'isSystem',
    width: 80, align: 'center', dataType: 'boolean',
    filters: [{ text: 'System', value: true }, { text: 'User', value: false }],
    filterField: 'is_system',
    render: (v) => v ? <Tag color="default" style={{ marginInlineEnd: 0 }}>System</Tag> : '—'
  },
  {
    title: 'Active', dataIndex: 'isActive', key: 'isActive',
    width: 70, align: 'center', dataType: 'boolean',
    filters: [{ text: 'Active', value: true }, { text: 'Inactive', value: false }],
    filterField: 'is_active',
    render: (v) => v === false
      ? <span className="erp-grid__muted">Inactive</span>
      : <span style={{ color: '#16a34a' }}>Active</span>
  }
];

export default function LedgerAccountsPage() {
  // Permissions live on `me.permissions` in the /me payload. The
  // `master` role's list is `['*']` (wildcard — see
  // userPermissions.js line 14), so a plain `.has(specific)` check
  // misses it. Treat `'*'` as "all permissions" the way the backend
  // does. Admins / managers / staff / viewer all carry explicit
  // permission strings.
  const { me } = useAuth();
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);
  const canCreate = hasPerm('erp.accounts.ledgers.create');
  const canUpdate = hasPerm('erp.accounts.ledgers.update');

  const { gridHandlers, refetch } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });
  const mergedColumns = useMemo(() => columns, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [modalRecord, setModalRecord] = useState(null);

  const openAdd = useCallback(() => {
    setModalMode('add');
    setModalRecord(null);
    setModalOpen(true);
  }, []);
  const openEdit = useCallback((record) => {
    setModalMode('edit');
    setModalRecord(record);
    setModalOpen(true);
  }, []);
  const openView = useCallback((record) => {
    setModalMode('view');
    setModalRecord(record);
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const onSaved = useCallback(async () => {
    setModalOpen(false);
    await refetch();
  }, [refetch]);

  // Per-row ⋯ menu: View always; Edit only if the operator has the
  // update permission. System rows still allow Edit since the modal
  // protects the immutable fields.
  const contextMenuActions = useMemo(() => {
    const items = [
      { key: 'view', label: 'View', icon: <EyeOutlined />, onClick: openView }
    ];
    if (canUpdate) {
      items.push({
        key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: openEdit
      });
    }
    return items;
  }, [openView, openEdit, canUpdate]);

  // ErpDataGrid hides the toolbar add / edit / view / delete buttons
  // unless explicitly enabled here. Without this prop the toolbar's
  // canAdd check (actions.add && permissions.add !== false && onAdd)
  // is false and the "+ New Ledger" button never renders — that's
  // the root cause of the "create option not available" bug.
  const actionsProp = useMemo(() => ({
    add:    canCreate,
    edit:   canUpdate,
    view:   true,
    delete: false
  }), [canCreate, canUpdate]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
      <Title level={4} style={{ marginTop: 0, marginBottom: 8 }}>Ledger Accounts</Title>
      <ErpDataGrid
        title="Chart of Accounts"
        gridId="accounts-ledgers-v1"
        columns={mergedColumns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        searchable
        searchPlaceholder="Search by code / name / group…"
        searchShortcut
        exportable
        exportFilename="ledger-accounts"
        actionWidth={56}
        actions={actionsProp}
        addButtonText="New Ledger"
        addButtonIcon={<PlusOutlined />}
        onAdd={canCreate ? openAdd : undefined}
        onEdit={canUpdate ? openEdit : undefined}
        onView={openView}
        onRowClick={openView}
        contextMenuActions={contextMenuActions}
        emptyTitle="No ledger accounts"
        emptyText="System ledgers seed automatically — if this list is empty, contact admin."
        emptyCtaLabel={canCreate ? 'New Ledger' : undefined}
        {...gridHandlers}
      />
      <LedgerModal
        open={modalOpen}
        mode={modalMode}
        record={modalRecord}
        onClose={closeModal}
        onSaved={onSaved}
      />
      </div>
    </ErpMasterShell>
  );
}
